/**
 * Motivos de encerramento de atendimento — fonte única para cliente e servidor.
 *
 * Vive em `shared/` porque três coisas precisam concordar sobre esta lista:
 * o `<Select>` do diálogo "Encerrar atendimento", o rótulo de leitura da
 * conversa já encerrada, e o `z.enum` de `inbox.closeConversation`. Antes eram
 * três cópias manuais, e o drift entre duas delas já produziu um bug real
 * (motivo inválido persistido ao trocar a resposta "foi resolvido?").
 *
 * O servidor deriva o enum daqui via `CLOSE_REASON_VALUES` — acrescentar um
 * motivo passa a ser uma edição só.
 */

export type CloseReason =
  | "resolved"
  | "client_confirmed"
  | "no_response"
  | "out_of_scope"
  | "duplicate"
  | "spam"
  | "transferred"
  | "other";

export type CloseReasonOption = { value: CloseReason; label: string };

/**
 * As duas listas NÃO são disjuntas — "transferred" e "other" valem nos dois
 * casos. A separação existe para oferecer primeiro o motivo que faz sentido
 * na resposta já dada, e para validar que o motivo escolhido continua válido
 * quando o atendente troca essa resposta.
 */
export const CLOSE_REASONS: Record<"resolved" | "unresolved", CloseReasonOption[]> = {
  resolved: [
    { value: "resolved", label: "Demanda atendida" },
    { value: "client_confirmed", label: "Cliente confirmou solução" },
    { value: "transferred", label: "Encaminhado a outra equipe" },
    { value: "other", label: "Outro" },
  ],
  unresolved: [
    { value: "no_response", label: "Cliente não respondeu" },
    { value: "out_of_scope", label: "Fora do escopo" },
    { value: "duplicate", label: "Atendimento duplicado" },
    { value: "spam", label: "Spam / erro de número" },
    { value: "transferred", label: "Encaminhado a outra equipe" },
    { value: "other", label: "Outro" },
  ],
};

/** Motivos aceitos pelo servidor. Deduplicado — "transferred"/"other" repetem. */
export const CLOSE_REASON_VALUES = [
  ...new Set([...CLOSE_REASONS.resolved, ...CLOSE_REASONS.unresolved].map((r) => r.value)),
] as [CloseReason, ...CloseReason[]];

const CLOSE_REASON_LABELS: Record<string, string> = Object.fromEntries(
  [...CLOSE_REASONS.resolved, ...CLOSE_REASONS.unresolved].map((r) => [r.value, r.label]),
);

/**
 * Rótulo de uma conversa já encerrada. `reason` chega como string crua do
 * banco: motivo antigo ou desconhecido cai no próprio valor em vez de sumir.
 */
export function closedReasonLabel(reason: string | null | undefined): string {
  if (!reason) return "Sem motivo registrado";
  return CLOSE_REASON_LABELS[reason] ?? reason;
}

/** Opções oferecidas para a resposta dada. Vazio enquanto ela não existe. */
export function closeReasonsFor(resolved: boolean | null): CloseReasonOption[] {
  if (resolved === null) return [];
  return CLOSE_REASONS[resolved ? "resolved" : "unresolved"];
}

/**
 * Troca a resposta "foi resolvido?" e descarta o motivo se ele não existe na
 * nova lista. Sem isso, escolher "Sim, resolvido" + "Demanda atendida" e
 * depois trocar para "Não foi resolvido" deixava `reason: "resolved"` no
 * estado: o gatilho ficava visualmente vazio (o item sumiu da lista) mas o
 * botão continuava habilitado, e a conversa era encerrada como não-resolvida
 * com motivo "Demanda atendida".
 */
export function pickResolved<T extends { resolved: boolean | null; reason: CloseReason | "" }>(
  state: T,
  resolved: boolean,
): T {
  const stillValid = closeReasonsFor(resolved).some((r) => r.value === state.reason);
  return { ...state, resolved, reason: stillValid ? state.reason : "" };
}
